import type { StoreApi } from "zustand"
import type { TableModel } from "utils/tableModel"
import { DashboardState } from "features/Dashboard/runtime/dashboardStore"
import { QueryQueue } from "features/Dashboard/runtime/queryQueue"
import { runTile } from "features/Dashboard/runtime/runTile"
import type { TileControlState } from "features/Dashboard/runtime/tileScript"
import { buildTileView } from "features/Dashboard/runtime/tileView"
import { orderedPanels, repeatOf } from "features/Dashboard/runtime/panels"
import {
  DataTile,
  Page,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"

export interface ExportEntry {
  tile: DataTile
  page: Page
  /** What the tile shows: its result with measures and category display. */
  model: TableModel
  /** The tile's last result as the engine returned it (a KPI base reads it). */
  rawModel?: TableModel
  /** The viewer's choices for the tile (its Statistic pick among them). */
  controls?: TileControlState
  /** Set on each panel of a repeated tile: the category the panel shows. */
  panel?: { category: string }
}

/** The data tiles of the chosen pages, in page order. */
export const chosenDataTiles = (
  state: DashboardState,
  pageIds: string[]
): { tile: DataTile; page: Page }[] =>
  state.document.pages
    .filter((p) => pageIds.includes(p.id))
    .flatMap((page) =>
      page.tiles.filter(isDataTile).map((tile) => ({ tile, page }))
    )

/**
 * The data tiles of the chosen pages that have a current result, in page
 * order, each with its view (see `buildTileView`) so the files show what
 * the tiles show. A tile that is not ready is left out even when it holds
 * a model: after an error or while stale, that model is from an earlier
 * run and may not match the filters in force. A tile showing a message
 * (nothing to run yet, or an output that is not a table) has no data and
 * is left out too. A repeated tile gives one entry per panel, in the order
 * of its variable's categories; a panel whose run failed is left out.
 */
export const collectExportTiles = (
  state: DashboardState,
  pageIds: string[]
): ExportEntry[] =>
  chosenDataTiles(state, pageIds).flatMap(({ tile, page }) => {
    const rt = state.tiles[tile.id]
    if (rt?.status !== "ready" || rt.message) return []
    const controls = state.tileControls[tile.id]
    if (rt.panels) {
      return orderedPanels(
        rt.panels,
        state.codebook,
        repeatOf(tile)?.variable
      ).flatMap(({ category, panel }) =>
        panel.model
          ? [
              {
                tile,
                page,
                model: buildTileView(tile, panel.model, controls),
                rawModel: panel.model,
                controls,
                panel: { category },
              },
            ]
          : []
      )
    }
    if (!rt.model) return []
    return [
      {
        tile,
        page,
        model: buildTileView(tile, rt.model, controls),
        rawModel: rt.model,
        controls,
      },
    ]
  })

/** How many tiles `entries` come from: a repeated tile's panels count once. */
export const exportedTileCount = (entries: ExportEntry[]): number =>
  new Set(entries.map((e) => e.tile.id)).size

/** One panel of a repeated tile as the PowerPoint export draws it. */
export interface ExportPanel {
  category: string
  /** The panel's view. */
  model: TableModel
  /** The panel's last result, which the colours follow. */
  rawModel?: TableModel
}

/**
 * The panels among `entries`, by tile id, in order: what the PowerPoint
 * export lays out as a grid in each repeated tile's place.
 */
export const exportPanels = (
  entries: ExportEntry[]
): Record<string, ExportPanel[]> => {
  const panels: Record<string, ExportPanel[]> = {}
  entries.forEach((e) => {
    if (!e.panel) return
    const list = panels[e.tile.id] ?? []
    list.push({
      category: e.panel.category,
      model: e.model,
      ...(e.rawModel ? { rawModel: e.rawModel } : {}),
    })
    panels[e.tile.id] = list
  })
  return panels
}

/**
 * Runs every data tile of the chosen pages that is not ready, with the
 * filters, cross-filters and tile controls in force, and resolves once each
 * run has settled. The runner only runs the active page, so the tiles of
 * other pages run here. A run that fails or is superseded leaves its tile
 * without a result, and the export skips it.
 */
export const ensureTilesReady = async (
  store: StoreApi<DashboardState>,
  queue: QueryQueue,
  pageIds: string[]
): Promise<void> => {
  const state = store.getState()
  const ids = chosenDataTiles(state, pageIds)
    .map(({ tile }) => tile.id)
    .filter((id) => state.tiles[id]?.status !== "ready")
  await Promise.allSettled(ids.map((id) => runTile(store, queue, id)))
}
